import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box, Button, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { ExportDataButton } from "components/ExportDataButton";
import { Importer } from "components/Importer";
import { ImportDropdown } from "components/Importer/ImportDropdown";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { OneSchemaTemplateType } from "encamp-shared/src/oneSchema/types";
import { FireDepartmentSearchQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import compact from "lodash-es/compact";
import { useMemo, useState } from "react";

import { FIRE_DEPARTMENT_SEARCH } from "./api";
import { ConfirmDeleteFireDepartments } from "./ConfirmDeleteFireDepartments";
import { CreateEditFireDepartmentDialog } from "./CreateEditDialog";

type Row = FireDepartmentSearchQuery["fireDepartmentSearch"]["items"][number];

type FireDepartmentsProps = {
  initialPageSize?: number;
};

export const FireDepartments: React.FC<FireDepartmentsProps> = ({ initialPageSize }) => {
  const {
    selectedId: editFireDepartmentId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("fireDepartmentId");

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmDeleteFireDepartment, setConfirmDeleteFireDepartment] = useState<Row | null>(null);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      { field: "id", headerName: "Id", width: 100 },
      { field: "name", headerName: "Name", flex: 3 },
      {
        field: "mailingAddress",
        headerName: "Mailing Address",
        flex: 2,
        valueGetter: (_, row) => {
          return compact([row.mailingAddress, row.mailingAddressLine2]).join(", ");
        },
      },
      { field: "city", headerName: "City", flex: 1 },
      { field: "county", headerName: "County", flex: 1 },
      { field: "state", headerName: "State/Territory", width: 75 },
      { field: "zip", headerName: "Zip", width: 75 },
      { field: "email", headerName: "Email", flex: 3 },
      {
        field: "issues",
        headerName: "Issues",
        flex: 0.3,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row: { issues } }) => {
          return <IssueCount issueCount={issues?.length} />;
        },
        sortable: false,
      },
      {
        field: "actions",
        type: "actions",
        align: "left",
        getActions: ({ row }) => {
          return [
            <Tooltip title="Edit Agency" key={1}>
              <GridActionsCellItem
                label="Edit Agency"
                onClick={() => pushIdToUrl(row.id)}
                icon={<Edit />}
              />
            </Tooltip>,
            <Tooltip title="Delete Agency" key={2}>
              <GridActionsCellItem
                label="Delete Agency"
                onClick={() => setConfirmDeleteFireDepartment(row)}
                icon={<Delete />}
              />
            </Tooltip>,
          ];
        },
      },
    ],
    [pushIdToUrl]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="fire-departments"
        isURLDriven
        columns={columns}
        dataQuery={FIRE_DEPARTMENT_SEARCH}
        getItems={(data: any) => data.fireDepartmentSearch.items ?? []}
        getCount={(data: any) => data.fireDepartmentSearch.count ?? []}
        noDataMessage="No fire departments found."
        initialPageSize={initialPageSize ?? 25}
        onRowClick={({ row }) => {
          pushIdToUrl(row.id);
        }}
        commandButtons={[
          <ImportDropdown
            key="import"
            importers={[
              {
                name: "Fire Departments",
                importer: (
                  <Importer
                    templateType={OneSchemaTemplateType.FireDepartment}
                    refetchQueries={["FireDepartmentSearch"]}
                  />
                ),
              },
            ]}
          />,
          <ExportDataButton
            key="export"
            exportType="fire-departments"
            fileName="fire-departments"
          />,
          <Button
            key="add-fire-department-button"
            variant="contained"
            onClick={() => setAddDialogOpen(true)}
          >
            Add Fire Department
          </Button>,
        ]}
      />
      {editFireDepartmentId && (
        <CreateEditFireDepartmentDialog
          onClose={popIdFromUrl}
          agencyType="Fire Department"
          mode="edit"
          fireDepartmentId={editFireDepartmentId}
        />
      )}
      {addDialogOpen && (
        <CreateEditFireDepartmentDialog
          onClose={() => setAddDialogOpen(false)}
          agencyType="Fire Department"
          mode="add"
        />
      )}

      {confirmDeleteFireDepartment && (
        <ConfirmDeleteFireDepartments
          fireDepartmentId={confirmDeleteFireDepartment.id}
          onClose={() => setConfirmDeleteFireDepartment(null)}
        />
      )}
    </Box>
  );
};
